'use client';

import { Bars3Icon } from '@heroicons/react/16/solid';
import { HeartIcon } from '@heroicons/react/24/solid';
import { useTranslations } from 'next-intl';
import { useRef } from 'react';

import { Button, HeroIcon } from '@/components/atoms';
import { LinkButton, NavbarBrand, NavbarLinks } from '@/components/molecules';
import { NavMenu } from '@/components/molecules/Navbar/Navbar';
import { LanguageSelector } from '@/components/organisms';
import { cn } from '@/lib/utils/tailwind';

import type { Language, Page } from '@/lib/api/types';

export interface DesktopNavProps {
  isDesktop: boolean;
  menuToggle: () => void;
  menuOpen: boolean;
  pages: Page[];
  languages: Language[];
  setDrawerOpen: (open: boolean) => void;
  drawerOpen?: boolean;
  authenticated: boolean;
  logoutAction?: () => void;
  loginAction?: () => void;
  logo: string;
  name: string;
  setIsOverflowing?: (overflow: boolean) => void;
}

export const DesktopNavbar = ({
  isDesktop,
  menuToggle,
  menuOpen,
  pages,
  languages,
  setDrawerOpen,
  drawerOpen = false,
  authenticated,
  logoutAction,
  loginAction,
  logo,
  name,
  setIsOverflowing,
}: DesktopNavProps) => {
  const t = useTranslations();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  return (
    <NavMenu
      className={cn(
        'px-(--horizontal-spacing-6)',
        'flex',
        'py-(--vertical-spacing-3)',
        'justify-between',
        'items-center',
        'border-b',
        'border-(--navigation-border-color)',
        'bg-(--navigation-background)',
        'min-h-[4.125rem]',
        'gap-(--horizontal-spacing-3)',
      )}
    >
      <NavbarBrand artwork={logo} name={name} />
      {isDesktop && (
        <div
          ref={wrapperRef}
          className="flex justify-center flex-1 min-w-0 overflow-hidden"
        >
          <NavbarLinks
            pages={pages}
            setIsOverflowing={setIsOverflowing}
            wrapperRef={wrapperRef}
          />
        </div>
      )}
      {isDesktop ? (
        <>
          {authenticated && (
            <LinkButton href={'/library'} size="sm" variant="secondary">
              <HeroIcon icon={HeartIcon} size="small" />
              {t('NAVBAR_LIBRARY')}
            </LinkButton>
          )}
          <LanguageSelector
            openMenu={menuOpen}
            action={menuToggle}
            languages={languages}
            placement="start"
          />
          {authenticated ? (
            <Button size="sm" variant="primary" onClick={logoutAction}>
              {t('NAVBAR_LOGOUT')}
            </Button>
          ) : (
            <Button size="sm" variant="primary" onClick={loginAction}>
              {t('B2B_SIGN_IN_CTA')}
            </Button>
          )}
        </>
      ) : (
        <Button
          variant={'ghost'}
          className={`ml-auto size-6 hover:bg-transparent focus:bg-transparent  active:bg-transparent ${isDesktop && 'hidden'}`}
          onClick={() => setDrawerOpen(true)}
          aria-label={t('OPEN_NAV_MENU')}
          aria-haspopup="dialog"
          aria-expanded={drawerOpen}
        >
          <HeroIcon
            className="text-(--navigation-menu-icon-color)"
            icon={Bars3Icon}
            size="small"
          />
        </Button>
      )}
    </NavMenu>
  );
};
